// Native Score transposition. Form harmony and every pitched content event move
// together, preserving parts, staves, voices, tuplets, spanners and event ids.

import { transposeChart } from '../chart';
import type { IRealChart } from '../ireal/types';
import {
  accidentalForKey,
  conventionalTonicName,
  parseKey,
  pitchClassOf,
  type KeyContext,
} from '../notes';
import { keySignatureOf, scoreToChart } from './convert';
import { midiOf, spellMidi } from './pitch';
import { mapReharmHarmony } from './reharm';
import type { KeySignature, NoteHead, Score, VoiceItem } from './types';

function nearestShift(semitones: number): number {
  const shift = ((semitones % 12) + 12) % 12;
  return shift > 6 ? shift - 12 : shift;
}

function transposeHead(head: NoteHead, semitones: number, key: KeyContext | null): NoteHead {
  if (semitones === 0) return { ...head, pitch: { ...head.pitch } };
  const prefer =
    head.pitch.alter < 0 ? 'flat' : head.pitch.alter > 0 ? 'sharp' : accidentalForKey(key);
  const midi = Math.max(0, Math.min(127, midiOf(head.pitch) + semitones));
  return { ...head, pitch: spellMidi(midi, { prefer }) };
}

function transposeItems(
  items: VoiceItem[],
  semitones: number,
  key: KeyContext | null,
): VoiceItem[] {
  return items.map((item) =>
    item.kind === 'tuplet'
      ? { ...item, items: transposeItems(item.items, semitones, key) }
      : {
          ...item,
          duration: { ...item.duration },
          notes: item.notes.map((head) => transposeHead(head, semitones, key)),
        },
  );
}

/**
 * Move every written note a whole octave, leaving the tune otherwise alone.
 *
 * Deliberately NOT `transposeScore(score, ±12)`: that routes through
 * `nearestShift`, which folds any interval into ±6 semitones so a key change
 * never drags the music up or down the stave. An octave folds to ZERO there,
 * which is why nothing in the app could move a score's register until now —
 * and why transposing down a fifth to "find a better fit" actually moves the
 * music UP a fourth.
 *
 * Spelling is preserved exactly (F♯ stays F♯, octave 4 → 3) because only the
 * octave field moves: no respelling decision arises, and the key signature and
 * harmony are untouched. The point is the register, nothing else.
 */
export function shiftScoreOctave(score: Score, octaves: number): Score {
  if (octaves === 0) return score;
  const shiftItems = (items: VoiceItem[]): VoiceItem[] =>
    items.map((item) =>
      item.kind === 'tuplet'
        ? { ...item, items: shiftItems(item.items) }
        : {
            ...item,
            duration: { ...item.duration },
            notes: item.notes.map((head) => ({
              ...head,
              pitch: { ...head.pitch, octave: head.pitch.octave + octaves },
            })),
          },
    );

  return {
    ...score,
    octaveShift: (score.octaveShift ?? 0) + octaves,
    parts: score.parts.map((part) => ({
      ...part,
      measures: part.measures.map((measure) => ({
        ...measure,
        voices: measure.voices.map((voice) => ({ ...voice, items: shiftItems(voice.items) })),
      })),
    })),
  };
}

function shiftedKeySignature(sig: KeySignature, shift: number): KeySignature {
  const tonic = conventionalTonicName((pitchClassOf(sig.tonic) + shift + 12) % 12, sig.mode);
  return { tonic, mode: sig.mode };
}

/** Transpose the complete score while retaining its native structure. */
export function transposeScore(score: Score, semitones: number, targetKey?: string): Score {
  const pitchClassShift = ((semitones % 12) + 12) % 12;
  const contentShift = nearestShift(semitones);
  const projected = scoreToChart(score);
  const transposedChart = transposeChart(projected, semitones, targetKey);
  const destinationKey = parseKey(transposedChart.key);
  const explicitOpeningKey = targetKey ? keySignatureOf(targetKey) : undefined;

  const form = score.form.map((measure, index) => {
    const next = {
      ...measure,
      harmony: transposedChart.measures[index]?.chords ?? measure.harmony.map((c) => ({ ...c })),
    };
    if (measure.keySig) {
      next.keySig =
        index === 0 && explicitOpeningKey
          ? explicitOpeningKey
          : shiftedKeySignature(measure.keySig, pitchClassShift);
    } else if (index === 0 && explicitOpeningKey) {
      next.keySig = explicitOpeningKey;
    }
    return next;
  });

  const parts = score.parts.map((part) => ({
    ...part,
    staves: part.staves.map((staff) => ({
      ...staff,
      tuning: staff.tuning ? [...staff.tuning] : undefined,
    })),
    measures: part.measures.map((measure) => ({
      ...measure,
      voices: measure.voices.map((voice) => ({
        ...voice,
        items: transposeItems(voice.items, contentShift, destinationKey),
      })),
    })),
  }));

  // Every layer and the captured originals move with the form, so switching
  // layers after a transposition lands in the new key.
  const reharm = score.reharm
    ? mapReharmHarmony(score.reharm, (chords, measureId) => {
        const source: IRealChart = {
          ...projected,
          measures: [{ id: measureId, chords: chords.map((c) => ({ ...c })) }],
        };
        return transposeChart(source, semitones, targetKey).measures[0].chords;
      })
    : undefined;

  return { ...score, form, parts, ...(reharm ? { reharm } : {}) };
}
